import { useTranslation } from 'react-i18next';
import { AlertTriangle, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { cn } from 'lib/utils';
import { SUPPORTED_LOCALES } from 'i18n';
import {
  CONTRACT_TYPES,
  EXPERIENCE_LEVELS,
  PROFILE_KINDS,
  TEAM_SIZE_RANGE,
  VEHICLE_TYPES,
  type ContractType,
  type ExperienceLevel,
  type ProfileKind,
  type VehicleType,
} from 'api/technicianImportContract';
import {
  closedListLabel,
  companyIsRequired,
  controlId,
  errorId,
  isRequired,
  languageLabel,
  validationMessage,
  type ClosedListField,
  type FormErrors,
  type ValidatedField,
} from './validation';

// A Radix item cannot carry '': this stands for "not specified" and never reaches the payload.
const UNSET = '__unset';

export interface GeneralValues {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  password: string;
  lang: string;
  /** F30 — codes of the closed lists, '' when not specified; the team size as typed. */
  contractType: ContractType | '';
  profileKind: ProfileKind | '';
  experienceLevel: ExperienceLevel | '';
  vehicleType: VehicleType | '';
  teamSize: string;
}

type ChoiceKey = 'contractType' | 'profileKind' | 'experienceLevel' | 'vehicleType';

const CHOICES: Record<ChoiceKey, { list: ClosedListField; codes: readonly string[] }> = {
  contractType: { list: 'contract', codes: CONTRACT_TYPES },
  profileKind: { list: 'profile', codes: PROFILE_KINDS },
  experienceLevel: { list: 'level', codes: EXPERIENCE_LEVELS },
  vehicleType: { list: 'vehicle', codes: VEHICLE_TYPES },
};

interface GeneralSectionProps {
  values: GeneralValues;
  onChange: (patch: Partial<GeneralValues>) => void;
  readOnly: boolean;
  /** Creation asks for a password; editing leaves the existing one alone. */
  creating: boolean;
  isActive: boolean;
  /** Edit mode only: the switch writes the form, so the status is saved with the record. */
  onToggleActive: (isActive: boolean) => void;
  /** Null means the Jira account is not provisioned — the record says so rather than implying it is. */
  externalId: string | null;
  /** The identifier as stored, so a rename can name both sides before it is saved. */
  storedUsername: string | null;
  /** Empty until the first save attempt: the record refuses to flag a field nobody has reached. */
  errors?: FormErrors;
}

/**
 * Identity and contact. The language field ships and D1 is closed: it edits `users.lang`, already
 * in the payload, so it adds no second source of truth. `company` and `phone` are required by the
 * API even though the table allows null — the four other roles have no company, so the rule
 * cannot be a table constraint.
 */
const GeneralSection = ({
  values,
  onChange,
  readOnly,
  creating,
  isActive,
  onToggleActive,
  externalId,
  storedUsername,
  errors = {},
}: GeneralSectionProps) => {
  const { t } = useTranslation();

  /**
   * Every field the client requires now carries the asterisk: one required without it — Prénom and
   * Nom, until this lot — is a trap the person only discovers on a refused save.
   */
  const field = (
    key: ValidatedField & keyof GeneralValues,
    label: string,
    {
      type = 'text',
      maxLength,
      required = isRequired(key, creating),
      min,
      max,
    }: { type?: string; maxLength?: number; required?: boolean; min?: number; max?: number } = {}
  ) => {
    const code = errors[key];
    return (
      <div className="space-y-1.5">
        <Label htmlFor={controlId(key)}>
          {label}
          {required && <span className="text-rose-500"> *</span>}
        </Label>
        <Input
          id={controlId(key)}
          type={type}
          value={values[key]}
          required={required}
          maxLength={maxLength}
          min={min}
          max={max}
          disabled={readOnly}
          aria-invalid={code ? true : undefined}
          aria-describedby={code ? errorId(key) : undefined}
          className={cn(code && 'border-rose-400 focus-visible:ring-rose-400 dark:border-rose-500')}
          onChange={(event) => onChange({ [key]: event.target.value })}
        />
        {code && (
          <p id={errorId(key)} className="text-xs text-rose-600 dark:text-rose-400">
            {validationMessage(t, code)}
          </p>
        )}
      </div>
    );
  };


  // Read mode states the label as text; edit mode offers the closed list plus "not specified".
  const choice = (key: ChoiceKey, label: string) => {
    const id = `technician-${key}`;
    const value = values[key];
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>{label}</Label>
        {readOnly ? (
          <Input id={id} value={value ? closedListLabel(t, CHOICES[key].list, value) : '—'} disabled />
        ) : (
          // `next &&`: the hidden native select fires one empty change before the options mount.
          <Select
            value={value || UNSET}
            onValueChange={(next) => next && onChange({ [key]: next === UNSET ? '' : next })}
          >
            <SelectTrigger id={id}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNSET}>{t('Not specified')}</SelectItem>
              {CHOICES[key].codes.map((code) => (
                <SelectItem key={code} value={code}>
                  {closedListLabel(t, CHOICES[key].list, code)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    );
  };

  // What the status means for the person, in both modes: the account is the mobile app access.
  const hint = isActive
    ? t('The technician can sign in to the mobile app and receive interventions.')
    : t('The technician can no longer sign in to the mobile app, nor receive interventions.');

  const dotAndLabel = (
    <span className="inline-flex items-center gap-[7px]">
      <span
        className={cn('h-[7px] w-[7px] rounded-full', isActive ? 'bg-emerald-500' : 'bg-zinc-400')}
        aria-hidden="true"
      />
      <span
        className={cn(
          'text-[13px] font-medium',
          isActive ? 'text-zinc-900 dark:text-zinc-50' : 'text-zinc-500 dark:text-zinc-400'
        )}
      >
        {isActive ? t('Active') : t('Inactive')}
      </span>
    </span>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <User className="h-4 w-4" aria-hidden="true" />
          {t('General information')}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        {/* `maxLength` mirrors the schema's own caps, so an over-long value cannot even be typed:
            a native cap needs no message, and the pair is still checked against the 120 the
            derived identifier has to fit in. */}
        {field('firstName', t('First name'), { maxLength: 120 })}
        {field('lastName', t('Last name'), { maxLength: 120 })}
        {field('phone', t('Phone'), { type: 'tel', maxLength: 30 })}
        {field('email', t('Email'), { type: 'email', maxLength: 255 })}

        {/* Read-only text in read mode, a select in edit mode. Never empty: an absent `lang` is
            resolved upstream, so the field always states a language rather than showing a blank. */}
        <div className="space-y-1.5">
          <Label htmlFor="technician-lang">{t('Profile language')}</Label>
          {readOnly ? (
            <Input id="technician-lang" value={languageLabel(t, values.lang)} disabled />
          ) : (
            // `lang &&`: inside a <form>, Radix mirrors the select into a hidden native one that
            // fires one empty change before the options mount — and an empty `lang` is a 400.
            <Select value={values.lang} onValueChange={(lang) => lang && onChange({ lang })}>
              <SelectTrigger id="technician-lang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_LOCALES.map((locale) => (
                  <SelectItem key={locale} value={locale}>
                    {languageLabel(t, locale)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {t('Prefilled from the country — you can change it')}
          </p>
        </div>

        {creating && field('password', t('Password'), { type: 'password' })}

        {/* The design's "Compte technicien" box, holding the derived identifier instead of the
            code it invented: three fields for two facts was the recette's complaint. It is
            disabled in both modes — a rename follows the name, never a hand. */}
        <div className="space-y-1.5 sm:col-span-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <Label htmlFor="technician-username">{t('Technician account')}</Label>
            <span className="text-[11.5px] text-zinc-500 dark:text-zinc-400">
              {creating ? t('Assigned automatically') : t('Identifier cannot be edited')}
            </span>
          </div>
          {/* Read mode states the identifier as it stands; edit mode states the one a save would
              write. Showing the derivation in read mode would name a login that does not exist. */}
          <Input
            id="technician-username"
            value={(readOnly && storedUsername) || values.username}
            disabled
            className="border-dashed bg-zinc-50 font-mono dark:bg-zinc-900/40"
          />
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {t('Derived from the first and last name, and capped at 120 characters. It is a sign-in identifier — the email stays the stable credential.')}
          </p>
          {!readOnly && !creating && !!storedUsername && storedUsername !== values.username && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {t('The stored identifier is {{from}}: the next save renames it to {{to}}.', {
                from: storedUsername,
                to: values.username,
              })}
            </p>
          )}
        </div>

        {/* The account status lives here, not on a footer button: read mode states it, edit mode
            toggles it, and either way it is the one place the card tells this truth. */}
        {readOnly ? (
          <div className="flex flex-col gap-[5px] rounded-[10px] border border-zinc-200 bg-zinc-50 px-3.5 py-3 dark:border-zinc-800 dark:bg-zinc-900/40">
            <span className="text-[11.5px] font-medium uppercase tracking-[0.05em] text-zinc-500 dark:text-zinc-400">
              {t('Account status')}
            </span>
            {dotAndLabel}
            <span className="text-xs leading-[1.4] text-zinc-500 dark:text-zinc-400">{hint}</span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3.5 rounded-[10px] border border-zinc-200 px-3.5 py-3 sm:col-span-2 dark:border-zinc-800">
            <div className="space-y-0.5">
              <p className="text-[13.5px] font-medium text-zinc-900 dark:text-zinc-50">
                {t('Account status')}
              </p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {creating ? `${hint} ${t('Active status by default on creation.')}` : hint}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {dotAndLabel}
              <Switch
                checked={isActive}
                onCheckedChange={onToggleActive}
                aria-label={t('Account status')}
              />
            </div>
          </div>
        )}

        <div className="grid gap-4 border-t border-zinc-200 pt-4 sm:col-span-2 sm:grid-cols-2 dark:border-zinc-800">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-zinc-500 sm:col-span-2 dark:text-zinc-400">
            {t('Contract and profile')}
          </p>
          {choice('contractType', t('Contract status'))}
          {field('company', t('Company'), {
            maxLength: 120,
            required: companyIsRequired(values.contractType, creating),
          })}
          {choice('profileKind', t('Profile'))}
          {choice('experienceLevel', t('Level'))}
          {field('teamSize', t('Team size'), {
            type: 'number',
            required: false,
            min: TEAM_SIZE_RANGE.min,
            max: TEAM_SIZE_RANGE.max,
          })}
          {choice('vehicleType', t('Vehicle'))}
        </div>

        {!creating && externalId === null && (
          <p className="sm:col-span-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {t('No Jira account is linked to this technician yet, so they cannot receive a request through the portal.')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default GeneralSection;
